import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlug, faBullhorn, faSitemap, faSpinner, faCheck } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { setAccountType } from "../../store/actions";
import { useAccountType } from "../../hooks/useAccountType";
import { apiErrorMessage } from "../../utils/partnerApi";
import { SECURITY_NOTICE_CLOSED_EVENT, securityNoticeDue } from "./SecurityAnnouncementModal";

// Set by usePrivyAuth.register for a brand-new account (value = its affiliate id); cleared once answered.
export const ONBOARDING_PENDING_KEY = "apOnboardingPending";

const OPTIONS = [
  {
    key: "partner",
    icon: faPlug,
    title: "Partner",
    kicker: "API & widget",
    desc: "Add swaps to your app or site: API keys, the embeddable widget, webhooks and whitelisting.",
    badge: "Default",
  },
  {
    key: "kol",
    icon: faBullhorn,
    title: "KOL / Creator",
    kicker: "Referral campaigns",
    desc: "Share referral links and campaigns, follow clicks, sign-ups and earnings. Developer tools stay out of the way.",
  },
  {
    key: "agency",
    icon: faSitemap,
    title: "Agency",
    kicker: "Manage KOLs",
    desc: "Invite KOL accounts and follow their campaigns and stats read-only. Your own earnings stay separate.",
  },
];

const MODE_TOAST = {
  partner: "Partner mode on. API and widget tools are in the menu.",
  kol: "KOL mode on. Campaigns and stats are in the menu.",
  agency: "Agency mode on. Invite KOL accounts from the Agency menu.",
};

// One-time question for a freshly created account, shown right after the 2FA notice closes:
// what is this dashboard for? Partner (API & widget) is the default, nothing changes unless chosen.
const AccountTypeOnboardingModal = () => {
  const dispatch = useDispatch();
  const authenticated = useSelector((s) => s.userReducer.authenticated);
  const twoFactor = useSelector((s) => s.userReducer.twoFactor);
  const affiliateId = useSelector((s) => s.userReducer.user?.response?.affiliateId) || localStorage.getItem("affiliateId") || "";
  const accountType = useAccountType();
  const [selected, setSelected] = useState("partner");
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [noticeDue, setNoticeDue] = useState(() => securityNoticeDue());
  const pending = localStorage.getItem(ONBOARDING_PENDING_KEY);
  const isPending = !answered && Boolean(pending) && (pending === "1" || String(pending) === String(affiliateId));

  // The 2FA notice goes first; re-check when it closes (same tab) or was answered elsewhere.
  useEffect(() => {
    const recheck = () => setNoticeDue(securityNoticeDue());
    window.addEventListener(SECURITY_NOTICE_CLOSED_EVENT, recheck);
    window.addEventListener("storage", recheck);
    return () => {
      window.removeEventListener(SECURITY_NOTICE_CLOSED_EVENT, recheck);
      window.removeEventListener("storage", recheck);
    };
  }, []);

  const securityNoticeOpen = twoFactor.loaded && !twoFactor.enabled && noticeDue;
  const show = authenticated && isPending && twoFactor.loaded && !securityNoticeOpen;

  const finish = useCallback(() => {
    localStorage.removeItem(ONBOARDING_PENDING_KEY);
    setBusy(false);
    setAnswered(true);
  }, []);

  const confirm = async () => {
    if (busy) return;
    if (selected === accountType) { finish(); return; }
    setBusy(true);
    try {
      await dispatch(setAccountType(selected));
      toast.success(MODE_TOAST[selected]);
      finish();
    } catch (e) {
      setBusy(false);
      toast.error(apiErrorMessage(e, "Could not save your choice. You can set it later from Account."));
    }
  };

  if (!show) return null;

  return (
    <div className="apOnbOverlay" role="dialog" aria-modal="true" aria-labelledby="apOnbTitle">
      <div className="apOnbCard">
        <div className="apOnbKicker">Welcome aboard</div>
        <h2 id="apOnbTitle" className="apOnbTitle">What will you use the dashboard for?</h2>
        <p className="apOnbLead">
          This only shapes the menu and the tools you see first. Your data and earnings are the same in every mode,
          and you can switch at any time from Account.
        </p>
        <div className="apOnbGrid">
          {OPTIONS.map((o) => {
            const on = selected === o.key;
            return (
              <button
                key={o.key}
                type="button"
                className={`apOnbOpt${on ? " on" : ""}`}
                onClick={() => setSelected(o.key)}
                disabled={busy}
                aria-pressed={on}
              >
                <span className="apOnbIcon"><FontAwesomeIcon icon={o.icon} /></span>
                {o.badge && <span className="apBdg new apOnbBadge">{o.badge}</span>}
                <span className="apOnbCheck" aria-hidden="true"><FontAwesomeIcon icon={faCheck} /></span>
                <span className="apOnbOptKicker">{o.kicker}</span>
                <span className="apOnbOptTitle">{o.title}</span>
                <span className="apOnbOptDesc">{o.desc}</span>
              </button>
            );
          })}
        </div>
        <div className="apOnbActions">
          <button type="button" className="btnPrimary" style={{ minWidth: 220, height: 46 }} onClick={confirm} disabled={busy}>
            {busy ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : `Continue as ${OPTIONS.find((o) => o.key === selected)?.title || "Partner"}`}
          </button>
          <button type="button" className="apOnbSkip" onClick={finish} disabled={busy}>
            Skip for now (Partner)
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountTypeOnboardingModal;
